import React from 'react';
import { Box, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { AskedBadge, RisingBadge, SeasonBadge } from './Badges';
import { TierBadge } from './OrderLineCard';
import {
  LIST_APPROVE_LABEL, LIST_DROP_LABEL, LIST_KEEP_LABEL, LIST_REJECT_LABEL, LIST_STOCK_BADGE,
  LIST_NO_LINK_WARNING, POOL_BADGE, POOL_FIXABLE_LINK, ProposalKindKey, evidenceNumbers, isSourced, ownedLine,
  ownedPieces, proposalLines, railLabel,
} from './counterList';
import { displayTitle, thumbTarget } from './format';
import { C, MONO, RADIUS, SHADOW } from './tokens';
import { RestockV2ListProposal, RestockV2ThumbTarget } from './types';

// One proposal, one decision. The 依據 sentence sits on the face of the card and is NEVER behind a
// fold — that was the 2026-09-15 correction: 「what is your 依據 for adding sku 3588?」 has to be
// answerable at a glance, or the yes/no is a coin flip.
//
// 建議加入 answers 加入清單 / 不要這款. 建議換下 is already ON the list, so its two answers are
// 換下 / 留著 — 留著 writes an approve, which is what makes it an owner_pick next time round.



const Verb: React.FC<{
  text: string; label: string; tone: 'go' | 'back'; solid?: boolean; disabled: boolean; onClick: () => void;
}> = ({ text, label, tone, solid = false, disabled, onClick }) => {
  const colour = tone === 'go' ? C.go : C.back;
  const wash = tone === 'go' ? C.goWash : C.backWash;
  return (
    <Box
      component="button" type="button" onClick={onClick} disabled={disabled} aria-label={label}
      sx={{
        flex: 1, minHeight: 40, appearance: 'none', cursor: 'pointer', borderRadius: '9px',
        fontSize: 13.5, fontWeight: 800, border: solid ? 0 : '1px solid', borderColor: colour,
        bgcolor: solid ? colour : wash, color: solid ? '#fff' : colour,
        '&:disabled': { opacity: 0.5, cursor: 'default' },
      }}
    >
      {text}
    </Box>
  );
};

interface ListProposalCardProps {
  row: RestockV2ListProposal;
  kind: ProposalKindKey;
  pending: boolean;
  // 加入清單 / 留著 — both write an approve.
  onApprove: (row: RestockV2ListProposal) => void;
  // 不要這款 (rejects for good) on an add; 換下 (deletes the row, back to 待決定) on a remove.
  onDecline: (row: RestockV2ListProposal) => void;
  // How to draw the SECOND 依據 line. 'rule' = a lane rule kept it out although the demand is real
  // (M01: 42 units in 14 days, blocked only by 全季款不進新季款) — neutral, informational. 'warn' =
  // it failed the evidence floor, which IS a warning. Drawing them alike would tell him a strong
  // seller is a weak candidate.
  subTone?: 'muted' | 'rule' | 'warn';
  // The style has no 進貨店家 tag AND the server says it has real demand. M01 moves 42 units in 14
  // days and reads 訂不到 only because nobody tagged its supplier — that is a job, not a dead end,
  // and the same gap has blocked autumn styles before. So the sentence names the FIX and is drawn
  // in the action colour rather than the refusal red. No link: the tag is edited where tags are
  // edited, and pointing at a page that cannot fix it would be worse than naming the fix.
  fixableLink?: boolean;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const ListProposalCard: React.FC<ListProposalCardProps> = ({
  row, kind, pending, onApprove, onDecline, subTone = 'muted', fixableLink = false, onImage,
}) => {
  const sourced = isSourced(row);
  const approveLabel = kind === 'remove' ? LIST_KEEP_LABEL : LIST_APPROVE_LABEL;
  const declineLabel = kind === 'remove' ? LIST_DROP_LABEL : LIST_REJECT_LABEL;
  // Both 依據 lines, ordered by the question this card answers. Neither is ever behind a fold.
  // A 線上上升 row's `reason` already CONTAINS its lane sentence (重新考慮…・線上上升：…), so it is
  // the one line; printing both would say the same numbers twice.
  const rising = row.proposal_kind === 'rising';
  const { headline, sub } = rising
    ? { headline: (row.reason || row.lane_reason || '').trim() || '線上上升', sub: '' }
    : proposalLines(row, kind);
  // 看更多 row: the engine did not select it, so it has no planned depth and no stock claim. The
  // badge says so on the face of the card — the section heading is not enough once he has scrolled
  // past it, and a card that looks like a 建議加入 card IS a 建議加入 card to the eye.
  const pool = kind === 'pool';
  // A 你已經有貨 row has no tier because the engine never picked it — show what it IS instead of
  // defaulting the chip to 主力, which would invent evidence.
  const stock = !pool && !rising && (kind === 'stock' || !row.tier);
  // No fact twice on one card: the numbers and owned lines only print what the 依據 lines have not
  // said, and the 客人問過 badge is a MARKER — it earns its slot only when no sentence says it.
  const evidence = `${headline} ${sub}`;
  const owned = ownedLine(row, evidence);
  const numbers = evidenceNumbers(row, evidence);
  const showAsked = Number(row.asked) > 0 && evidence.indexOf('客人問過') < 0;
  return (
    <Box
      component="article" data-proposal={row.sku} data-kind={kind} data-cat={row.cat}
      sx={{
        bgcolor: C.card,
        // A pool card is DASHED: readable as 「not the engine's pick」 from across the desk, before a
        // single word is read. Same information as the badge, at a different distance.
        border: pool ? '1px dashed' : '1px solid',
        borderColor: pool ? C.line2 : C.line, borderRadius: `${RADIUS}px`,
        p: 1.1, boxShadow: SHADOW, display: 'flex', flexDirection: 'column',
        // Cards in a row stretch to the tallest; the verb pair is pinned to the bottom so three
        // cards side by side share one button baseline instead of three.
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
        <ProductThumb
          target={thumbTarget({ sku: row.sku, title: row.title, image_url: row.image_url ?? null })}
          width={74} height={99} radius={7} onOpen={onImage}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
            {row.tier ? <TierBadge tier={row.tier} /> : null}
            {rising && <RisingBadge reconsider={row.reconsider} />}
            {pool && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block', fontSize: 10.5, fontWeight: 800, px: '6px', mb: '3px',
                  borderRadius: '99px', whiteSpace: 'nowrap', bgcolor: C.waitWash, color: C.wait,
                  border: '1px dashed', borderColor: C.wait,
                }}
              >
                {POOL_BADGE}
              </Box>
            )}
            {stock && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block', fontSize: 10.5, fontWeight: 800, px: '6px', mb: '3px',
                  borderRadius: '99px', whiteSpace: 'nowrap', bgcolor: C.fetchWash, color: C.fetch,
                  border: '1px solid', borderColor: C.fetch,
                }}
              >
                {LIST_STOCK_BADGE}
              </Box>
            )}
            <SeasonBadge value={row.season} />
            <AskedBadge asked={showAsked} />
            <Typography
              component="span"
              sx={{
                fontSize: 10.5, fontWeight: 800, borderRadius: '99px', px: '6px', mb: '3px',
                bgcolor: row.on_rail ? C.paper2 : C.wash, color: C.ink2,
                border: '1px solid', borderColor: C.line,
              }}
            >
              {railLabel(row.on_rail)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>
            {displayTitle(row.title)}
          </Typography>
          {/* 依據 — always visible, never folded. */}
          <Typography data-evidence={row.sku} sx={{ fontSize: 12, color: C.ink2, mt: '2px', lineHeight: 1.45 }}>
            {headline}
          </Typography>
          {sub && (
            <Typography
              data-sub-tone={subTone}
              sx={{
                fontSize: 11.5, mt: '1px', lineHeight: 1.45,
                color: subTone === 'warn' ? C.wait : (subTone === 'rule' ? C.ink2 : C.ink3),
                ...(subTone === 'rule'
                  ? { borderLeft: '2px solid', borderColor: C.line2, pl: '5px' }
                  : {}),
              }}
            >
              {sub}
            </Typography>
          )}
          {numbers && (
            <Typography
              sx={{
                fontSize: 11.5, color: C.ink3, mt: '1px',
                overflowWrap: 'normal', wordBreak: 'keep-all',
              }}
            >
              {numbers}
            </Typography>
          )}
          {/* 2026-10-04 rule 7: an AW proposal while the projected rail is past the AW ceiling. Still
              approvable — the sentence only says why the engine would wait. */}
          {row.deferred_reason && (
            <Typography data-deferred={row.sku} sx={{ fontSize: 11.5, fontWeight: 700, color: C.wait, mt: '2px' }}>
              {row.deferred_reason}
            </Typography>
          )}
          {/* The quantity he already owns — the whole 依據 of a 你已經有貨 decision. */}
          {owned && (
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.fetch, mt: '2px' }}>{owned}</Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', mt: '3px' }}>
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
            <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.cat}</Typography>
            {typeof row.listed_days === 'number' && (
              <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>上架 {row.listed_days} 天</Typography>
            )}
          </Box>
          {/* `isSourced` answers 訂得到嗎 off the 下單連結 and the 台灣倉庫 lane — never off the
              進貨店家 tag, and never off a fact the payload simply did not carry. So an MdoC row
              (M01 M02 M04 M11 M16 M17 M29 M33: Taiwan stock that is never ordered from Korea and
              needs no link) can no longer render this block at all, which is what it had been
              wrongly doing on the 看更多 pool cards. */}
          {!sourced && (
            <Typography
              data-unsourced={fixableLink ? 'fixable' : 'blocked'}
              sx={{
                fontSize: 11.5, fontWeight: 700, mt: '3px',
                color: fixableLink ? C.fetch : C.back,
              }}
            >
              {fixableLink ? POOL_FIXABLE_LINK : LIST_NO_LINK_WARNING}
            </Typography>
          )}
        </Box>
        {/* A stock row's number is what he ALREADY owns; an engine row's is what approving buys.
            A POOL row has neither: the engine never planned it, so there is no piece count to
            print. 「引擎放 0 件」 would be an engine opinion that does not exist, so the block is
            omitted entirely rather than zeroed. */}
        {!pool && (
          <Box
            aria-label={stock ? `已經有 ${ownedPieces(row)} 件` : `引擎會放 ${row.depth} 件`}
            sx={{ textAlign: 'right', flex: 'none' }}
          >
            <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>{stock ? '已經有' : '引擎放'}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1.1 }}>
              {stock ? ownedPieces(row) : row.depth}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.9, mt: 'auto', pt: 1.1 }}>
        <Verb
          text={approveLabel} label={`${approveLabel} ${row.sku}`} tone="go" solid={kind === 'add'}
          disabled={pending} onClick={() => onApprove(row)}
        />
        <Verb
          text={declineLabel} label={`${declineLabel} ${row.sku}`} tone="back" disabled={pending}
          onClick={() => onDecline(row)}
        />
      </Box>
    </Box>
  );
};

export default ListProposalCard;
